import { useState, useEffect, useMemo } from 'react';
import { FileText, Plus, Edit, Trash2, Eye, X, Loader2, Search } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import communityAPI from '../../../api/community';
import { message } from 'antd';
import { sanitizeRichText } from '../../../utils/sanitizeHtml';

const DEFAULT_FORM = { id: null, document_type: 'terms', title: '', content: '', version: '', is_active: true };
const FORM_ID = 'legal-document-form';

const getDarkModePreference = () => {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
};

const toFormShape = (doc) => ({
  ...DEFAULT_FORM,
  id: doc.id || null,
  document_type: doc.document_type || 'terms',
  title: doc.title || '',
  content: doc.content || '',
  version: doc.version || '',
  is_active: typeof doc.is_active === 'boolean' ? doc.is_active : true,
});

const formatDate = (value) => {
  if (!value) return 'Not yet updated';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not yet updated' : parsed.toLocaleString();
};

export default function LegalDocumentManager() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(() => ({ ...DEFAULT_FORM }));
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => getDarkModePreference());
  const [docPendingDelete, setDocPendingDelete] = useState(null);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const sanitizedPreviewContent = useMemo(() => {
    if (!previewDoc?.content) return '';
    return sanitizeRichText(previewDoc.content);
  }, [previewDoc?.content]);

  useEffect(() => {
    fetchDocuments(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleThemeChange = () => {
      setIsDarkMode(getDarkModePreference());
    };

    const observer = new MutationObserver(handleThemeChange);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
    };
  }, []);

  const fetchDocuments = async (showLoader = false) => {
    if (showLoader || documents.length === 0) {
      setLoading(true);
    }
    try {
      const res = await communityAPI.legal.getAll();
      setDocuments(res.data.results || res.data);
    } catch (err) {
      console.error('Failed to fetch documents', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const wasEditing = Boolean(editing);
    setSubmitting(true);
    const payload = { ...formData };
    if ('id' in payload) {
      delete payload.id;
    }
    const documentTitle = formData.title || 'Document';
    try {
      if (editing) {
        const targetId = editing.id;
        if (!targetId) {
          throw new Error('Document identifier missing. Refresh and try again.');
        }
        await communityAPI.legal.update(targetId, payload);
      } else {
        await communityAPI.legal.create(payload);
      }
      await fetchDocuments();
      resetForm();
      message.success(`${documentTitle} ${wasEditing ? 'updated' : 'created'} successfully.`);
    } catch (err) {
      const detail =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        'The server returned an unexpected error. Please try again.';
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRequest = (doc) => {
    setDocPendingDelete(doc);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    if (!docPendingDelete) return;
    setDeleteProcessing(true);
    setDeleteError('');
    try {
      if (!docPendingDelete.id) {
        setDeleteError('Unable to resolve document identifier. Please refresh and try again.');
        return;
      }
      await communityAPI.legal.delete(docPendingDelete.id);
      await fetchDocuments();
      message.success(`${docPendingDelete.title} deleted successfully.`);
      setDocPendingDelete(null);
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'The server could not process the request (500). Please check the document status and try again.');
    } finally {
      setDeleteProcessing(false);
    }
  };

  const closeDeleteModal = () => {
    if (deleteProcessing) return;
    setDocPendingDelete(null);
    setDeleteError('');
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ ...DEFAULT_FORM });
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({ ...prev, content }));
  };

  const handleEditorSave = () => {
    if (typeof document === 'undefined') return;
    document.getElementById(FORM_ID)?.requestSubmit();
  };

  const filteredDocuments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const bySearch = (doc) =>
      !normalizedSearch ||
      doc.title?.toLowerCase().includes(normalizedSearch) ||
      doc.document_type?.toLowerCase().includes(normalizedSearch) ||
      doc.document_type_display?.toLowerCase().includes(normalizedSearch);

    const byType = (doc) => typeFilter === 'all' || doc.document_type === typeFilter;
    const byStatus = (doc) =>
      statusFilter === 'all' ||
      (statusFilter === 'active' && doc.is_active) ||
      (statusFilter === 'inactive' && !doc.is_active);

    const parseSortValue = (value) => {
      if (!value) return 0;
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    return [...documents]
      .filter((doc) => bySearch(doc) && byType(doc) && byStatus(doc))
      .sort((a, b) => parseSortValue(b.updated_at || b.created_at) - parseSortValue(a.updated_at || a.created_at));
  }, [documents, searchTerm, typeFilter, statusFilter]);

  const summaryCards = useMemo(() => {
    const total = documents.length;
    const active = documents.filter((doc) => doc.is_active).length;
    const inactive = total - active;
    return [
      { label: 'Total documents', value: total, accent: 'bg-primary/10 text-primary dark:bg-primary/20' },
      { label: 'Active', value: active, accent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
      { label: 'Inactive', value: inactive, accent: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
    ];
  }, [documents]);

  if (loading)
    return (
      <div className="space-y-4 p-6">
        <div className="animate-pulse rounded-xl bg-white/60 p-6 shadow-sm dark:bg-[#161616]/40">
          <div className="mb-4 h-5 w-48 rounded bg-gray-200 dark:bg-[#161616]" />
          <div className="grid gap-4 sm:grid-cols-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="h-20 rounded-lg bg-gray-200 dark:bg-[#0E0E0E]" />
            ))}
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {[...Array(2)].map((_, index) => (
            <div key={index} className="space-y-3 rounded-xl bg-white/60 p-6 shadow-sm dark:bg-[#161616]/40">
              <div className="h-5 w-3/4 rounded bg-gray-200 dark:bg-[#0E0E0E]" />
              <div className="h-4 w-full rounded bg-gray-200 dark:bg-[#0E0E0E]" />
              <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-[#0E0E0E]" />
              <div className="h-10 w-full rounded bg-gray-200 dark:bg-[#0E0E0E]" />
            </div>
          ))}
        </div>
      </div>
    );

  return (
    <div className="p-6">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Legal Documents</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Maintain the policies your community relies on with quick edits and previews.</p>
        </div>
        <button
          onClick={resetForm}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white shadow-sm transition hover:bg-primary/90 hover:shadow"
        >
          <Plus size={18} /> New Document
        </button>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {summaryCards.map((card) => (
          <div key={card.label} className={`rounded-xl border border-white/40 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-gray-800 dark:bg-[#161616] ${card.accent}`}>
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{card.label}</p>
            <p className="mt-2 text-2xl font-semibold">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-[#161616]">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by title or type"
            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-800 dark:bg-[#0E0E0E] dark:text-gray-100"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="min-w-[160px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-800 dark:bg-[#0E0E0E] dark:text-gray-100"
        >
          <option value="all">All types</option>
          <option value="terms">Terms of Service</option>
          <option value="privacy">Privacy Policy</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="min-w-[160px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-800 dark:bg-[#0E0E0E] dark:text-gray-100"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          {filteredDocuments.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500 dark:border-gray-800 dark:bg-[#161616] dark:text-gray-400">
              <FileText size={28} className="mb-3 text-gray-300 dark:text-gray-700" />
              <p className="text-sm font-medium">No documents match the current filters.</p>
              <p className="text-xs">Try adjusting the search or create a new document to get started.</p>
            </div>
          ) : (
            filteredDocuments.map((doc) => (
              <div key={doc.id || doc.document_type} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-gray-800 dark:bg-[#161616]">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <FileText className="mt-1 text-primary" size={20} />
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{doc.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">v{doc.version} • {doc.document_type_display}</p>
                      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Updated {formatDate(doc.updated_at || doc.created_at)}</p>
                      <span
                        className={`mt-3 inline-block rounded-full px-3 py-1 text-xs font-medium ${
                          doc.is_active
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        {doc.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewDoc(doc)}
                      className="rounded-lg border border-transparent p-2 text-gray-500 transition hover:border-gray-200 hover:bg-gray-50 dark:text-gray-400 dark:hover:border-gray-700 dark:hover:bg-[#0E0E0E]"
                      title="Preview"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setEditing(doc);
                        setFormData(toFormShape(doc));
                      }}
                      className="rounded-lg border border-transparent p-2 text-gray-500 transition hover:border-gray-200 hover:bg-gray-50 dark:text-gray-400 dark:hover:border-gray-700 dark:hover:bg-[#0E0E0E]"
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteRequest(doc)}
                      className="rounded-lg border border-transparent p-2 text-red-500 transition hover:border-red-100 hover:bg-red-50 dark:text-red-400 dark:hover:border-red-900/50 dark:hover:bg-red-900/10"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-[#161616]">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{editing ? 'Edit Document' : 'New Document'}</h3>
          <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
              <select value={formData.document_type} onChange={(e) => setFormData({ ...formData, document_type: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 p-2 dark:border-gray-800 dark:bg-[#0E0E0E] dark:text-white" disabled={Boolean(editing)}>
                <option value="terms">Terms of Service</option>
                <option value="privacy">Privacy Policy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 p-2 dark:border-gray-800 dark:bg-[#0E0E0E] dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Version</label>
              <input type="text" value={formData.version} onChange={(e) => setFormData({ ...formData, version: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 p-2 dark:border-gray-800 dark:bg-[#0E0E0E] dark:text-white" placeholder="1.0" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
              <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
                <RichTextEditor
                  isDarkMode={isDarkMode}
                  fieldName="content"
                  value={formData.content}
                  onChange={handleEditorChange}
                  placeholder="Compose the policy with headings, links, and lists..."
                  onSave={handleEditorSave}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" />
              <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">Active</label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/60"
                disabled={submitting}
              >
                {submitting && <Loader2 size={16} className="animate-spin" />} {submitting ? 'Saving...' : 'Save'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-lg border border-gray-300 px-4 py-2 transition hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-[#0E0E0E]"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {previewDoc && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-6">
          <div className="relative h-[80vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#161616]">
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-800 dark:bg-[#0E0E0E]">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Preview</p>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{previewDoc.title}</h4>
                <span className="text-xs text-gray-500 dark:text-gray-400">v{previewDoc.version} • {previewDoc.document_type_display}</span>
              </div>
              <button
                onClick={() => setPreviewDoc(null)}
                className="rounded-full p-2 text-gray-500 transition hover:bg-gray-200/60 dark:text-gray-400 dark:hover:bg-[#161616]"
                aria-label="Close preview"
              >
                <X size={18} />
              </button>
            </div>
            <div className="h-full overflow-y-auto px-6 py-5 text-sm leading-relaxed text-gray-700 dark:text-gray-200">
              {/* Preview uses sanitized HTML to preserve formatting without risking XSS. */}
              <div dangerouslySetInnerHTML={{ __html: sanitizedPreviewContent }} />
            </div>
          </div>
        </div>
      )}

      {docPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl dark:bg-[#161616]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-800">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Delete document?</h4>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                This will permanently remove <span className="font-medium text-gray-900 dark:text-gray-100">{docPendingDelete.title}</span>.
              </p>
            </div>
            <div className="px-6 py-5 text-sm text-gray-600 dark:text-gray-300">
              <p>Document type: <span className="font-medium capitalize">{docPendingDelete.document_type_display || docPendingDelete.document_type}</span></p>
              <p className="mt-1">Version: <span className="font-medium">{docPendingDelete.version || 'Unspecified'}</span></p>
              <p className="mt-1">Status: <span className="font-medium">{docPendingDelete.is_active ? 'Active' : 'Inactive'}</span></p>
              {deleteError && <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">{deleteError}</p>}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-[#0E0E0E]"
                disabled={deleteProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-600/70"
                disabled={deleteProcessing}
              >
                {deleteProcessing && <Loader2 size={16} className="animate-spin" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
